// Shared shaping + formatting for the athlete activity panel (drawer and inline views).

import { intlLocale } from '../../i18n/locale-format';
import { ContestActivityIntegrity } from '../../models/types';

// Integrity fields are only present when the viewer is a contest admin.
export interface AthleteActivityItem extends ContestActivityIntegrity {
  id: string; // contest activity record id
  distance: number; // km
  pace: number; // decimal min/km
  movingTime: number; // seconds
  workoutType: string;
  startDate: string | Date;
  stravaActivityId?: number;
}

export interface AthleteDayGroup {
  key: string;
  label: string;
  activities: AthleteActivityItem[];
  totalDistance: number;
  totalTime: number;
}

export interface AthleteStats {
  totalDistance: number;
  activeDays: number;
  averagePace: number;
  bestPace: number;
}

const dayKey = (d: string | Date) => new Date(d).toDateString();

export const formatPace = (pace: number): string => {
  if (!pace || pace <= 0 || !isFinite(pace)) return '--';
  let minutes = Math.floor(pace);
  let seconds = Math.round((pace - minutes) * 60);
  if (seconds === 60) {
    minutes += 1;
    seconds = 0;
  }
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

export const formatDuration = (totalSeconds: number): string => {
  const s = Math.max(0, Math.round(totalSeconds || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`;
};

export const formatStartTime = (d: string | Date): string =>
  new Date(d).toLocaleTimeString(intlLocale(), { hour: 'numeric', minute: '2-digit' });

export const computeStats = (activities: AthleteActivityItem[]): AthleteStats => {
  const totalDistance = activities.reduce((sum, a) => sum + a.distance, 0);
  const totalTime = activities.reduce((sum, a) => sum + (a.movingTime || 0), 0);
  const paces = activities.map(a => a.pace).filter(p => p > 0);
  return {
    totalDistance,
    activeDays: new Set(activities.map(a => dayKey(a.startDate))).size,
    averagePace: totalDistance > 0 ? totalTime / 60 / totalDistance : 0,
    bestPace: paces.length > 0 ? Math.min(...paces) : 0,
  };
};

// Days newest first, activities within a day oldest first.
export const groupActivitiesByDay = (activities: AthleteActivityItem[]): AthleteDayGroup[] => {
  const map = new Map<string, AthleteActivityItem[]>();
  activities.forEach(a => {
    const k = dayKey(a.startDate);
    map.set(k, [...(map.get(k) || []), a]);
  });
  return Array.from(map.entries())
    .map(([key, items]) => ({
      key,
      label: new Date(key).toLocaleDateString(intlLocale(), { weekday: 'short', month: 'short', day: 'numeric' }),
      activities: [...items].sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()),
      totalDistance: items.reduce((sum, a) => sum + a.distance, 0),
      totalTime: items.reduce((sum, a) => sum + (a.movingTime || 0), 0),
    }))
    .sort((a, b) => new Date(b.key).getTime() - new Date(a.key).getTime());
};
